import { useMutation, useQuery } from "@tanstack/react-query";
import { httpClient, ROOT_URL } from "../App";
import { AxiosResponse } from "axios";

export interface IWalletBody {
  name: string;
  currencyId: string;
  assetConfigGroupId: string;
  address: string;
}

export async function handleConnect() {
  const wallets = await window.ethereum.request({
    method: "eth_requestAccounts",
  });

  if (wallets.length > 0) {
    await httpClient.post(`${ROOT_URL}/api/v1/blockchain/wallet`, {
      wallet: wallets[0],
    });
    return wallets[0];
  }
}

export const useGetListWallet = (params: {
  companyId: number;
  start: number;
  end: number;
  name?: string;
  currencyId?: number;
  ignoreId?: string;
}) => {
  return useQuery<{
    data: {
      data: {
        total: number;
        page: number;
        pageSize: number;
        totalPages: number;
        items: [];
      };
    };
  }>(
    [
      `/api/v1/wallets/company-wallets/get-list`,
      params.companyId,
      params.start,
      params.end,
      params.name,
      params.currencyId,
      params.ignoreId,
    ],
    async () => getListWallet(params),
    {
      enabled: !!params.companyId,
    }
  );
};

export const useGetListWalletProvider = (params: {
  companyId: number;
  start: number;
  end: number;
  name?: string;
  currencyId?: number;
}) => {
  return useQuery<{
    data: {
      data: {
        total: number;
        page: number;
        pageSize: number;
        totalPages: number;
        items: [];
      };
    };
  }>(
    [
      `/api/v1/wallets/company-wallets/get-list/provider`,
      params.companyId,
      params.start,
      params.end,
      params.name,
      params.currencyId,
    ],
    async () => getListWallet(params),
    {
      enabled: !!params.companyId,
    }
  );
};

export function getListWallet({
  companyId,
  start,
  end,
  name,
  currencyId,
  ignoreId,
}: {
  companyId: number;
  start: number;
  end: number;
  name?: string;
  currencyId?: number;
  ignoreId?: string;
}) {
  const params = new URLSearchParams();
  params.append("_start", String(start));
  params.append("_end", String(end));
  if (name) params.append("_like", name);
  if (currencyId) params.append("_currencyId", String(currencyId));
  if (ignoreId) params.append("_ignoreId", String(ignoreId));
  return httpClient.get(
    `${ROOT_URL}/api/v1/company-wallets/companies/${companyId}/get-list?${params.toString()}`
  );
}

export const useCreateWallet = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: createWallet,
  });
};

export function createWallet(body: IWalletBody): Promise<AxiosResponse<any>> {
  return httpClient.post(`${ROOT_URL}/api/v1/company-wallets/create`, body);
}

export const useUpdateWallet = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: updateWallet,
  });
};

export function updateWallet(
  body: IWalletBody & { id: number }
): Promise<AxiosResponse<any>> {
  return httpClient.patch(`${ROOT_URL}/api/v1/company-wallets/update`, body);
}

export const useDeleteWallet = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: deleteWallet,
  });
};

export function deleteWallet(id: number | null) {
  return httpClient.delete(`${ROOT_URL}/api/v1/company-wallets/${id}`);
}

export const useGetDetailWallet = (id: number) => {
  return useQuery<{ data: { data: any } }>(
    [`/api/v1/wallets/:id`, id],
    async () => getDetailWallet(id),
    {
      enabled: !!id,
    }
  );
};

export function getDetailWallet(id: number | null) {
  return httpClient.get(`${ROOT_URL}/api/v1/company-wallets/${id}`);
}
